import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled, { theme, prop } from '@nobia/zeus-components/lib/styled'
import settings from '@nobia/zeus-components/lib/settings'
import { Icon } from '../icons'
import { formatPrice } from '../cards/utils'
import { useTranslation } from '../../utils/useTranslation'
import {
  financeOptions,
  getMonthlyPayment,
  getYearsToPay,
  filterMonthsToPay,
} from '../../brands/monthsToPay'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  padding-bottom: 40px;
  width: ${p => `${p.elementWidth}px`};
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    margin: 0 0px 0px 90px;
  }
  @media (min-width: ${prop('theme.body.desktopWidth')}) {
    margin: 0 0px 0px 105px;
  }
  @media (min-width: 1200px) {
    margin: 0 0px 0px 107px;
  }
`

const H4 = styled.h4`
  font-size: 21px;
  margin: 27px 0 20px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 81%;
  font-family: ${prop('theme.font.paragraph.fontFamily')};
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    align-items: baseline;
  }
  @media (max-width: ${prop('theme.body.tabletWidth')}) {
    width: 100%;
  }
`

const SelectRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  .custom-select-container {
    display: flex;
    flex-direction: column;
    min-width: 200px;
    max-width: 400px;
    justify-content: space-between;
    align-items: center;
    padding: 0;
    margin: 0 10px 0 0;
    @media (min-width: ${prop('theme.body.desktopWidth')}) {
      margin: 0 20px;
    }
    @media (min-width: 1200px) {
      width: 80%;
    }
    span {
      display: none;
    }
    select {
      opacity: 1;
    }
  }
`

const Select = styled.select`
  background-image: ${({ backgroundUrl }) => `url(${backgroundUrl})`};
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-repeat: no-repeat, repeat;
  background-position: right 6px top 50%, 0 0;
  background-size: 1.66em auto, 100%;
  padding: 10px;
  font-size: 16px;
  font-family: ${prop('theme.font.select.fontFamily')};
  font-weight: ${prop('theme.font.select.fontWeight')};
  border-radius: 3px;
  border: 1px solid #c2bcb8;
  min-width: 220px;
  margin: 0 0 15px;
  max-width: 400px;
  background-color: ${theme('colors.white')};
  color: ${theme('colors.primary')};
  width: 100%;
  opacity: 1;
  cursor: pointer;
`

const P = styled.p`
  display: flex;
  justify-content: space-between;
  text-align: left;
  margin: 0;
  font-size: ${prop('theme.costSummary.totalCost.fontSize')};
`

const InfoIcon = styled(Icon)`
  margin-bottom: -3px;
  margin-right: 5px;
  overflow: visible;

`

const Strong = styled.strong`
  font-size: ${prop('theme.costSummary.totalCost.fontSize')};
  font-weight: 700;
`

const FinanceSelection = ({
  selection,
  setShowModal,
  summary,
  financeOption,
  financeId,
  handleFinanceOption,
  handleMonths,
  monthsToPayOptions,
  months,
  elementWidth,
}) => {
  const { t, country } = useTranslation()
  let availableOptions = financeOptions

  if (settings.brand === 'magnet' && !selection.range.ifcAvailable) {
    availableOptions = financeOptions.filter(
      option => option.id !== financeOptions[1].id
    )
  } else if (summary < 5000 && settings.brand !== 'magnet') {
    availableOptions = financeOptions.filter(
      option => option.id !== financeOptions[1].id
    )
  }

  const [monthsToPay, setMonthsToPay] = useState(
    filterMonthsToPay(summary, monthsToPayOptions, financeId)
  )

  const onFinanceChange = e => {
    const selectedFinance = financeOptions.find(
      option => option.title === e.target.value
    )
    handleFinanceOption(selectedFinance)
  }

  useEffect(() => {
    const filteredMonths = filterMonthsToPay(
      summary,
      monthsToPayOptions,
      financeId
    )

    if (financeOptions.length && financeOption.id === 'IFC' && summary < 20000 && settings.brand !== 'magnet') {
      handleFinanceOption(financeOptions[0]);
    }

    const financeOptionStillValid = availableOptions.some(option => option.id === financeOption.id)
    if (!financeOptionStillValid) {
      handleFinanceOption(financeOptions[0])
    }

    setMonthsToPay(filteredMonths)
    if (!filteredMonths.includes(months)) {
      handleMonths(filteredMonths[filteredMonths.length - 1])
    }
  }, [financeOption, summary])

  return (
    <Wrapper elementWidth={elementWidth}>
      <H4>{t('app.components.finance-selection.title')}</H4>
      <SelectRow>
        <Select
          backgroundUrl={t('app.components.summary.icon-url')}
          elementWidth={elementWidth}
          onChange={onFinanceChange}
        >
          {availableOptions.map(option => (
            <option key={option.id}>{option.title}</option>
          ))}
        </Select>
      </SelectRow>
      <SelectRow>
        <Select
          backgroundUrl={t('app.components.summary.icon-url')}
          onChange={e =>
            handleMonths(monthsToPay.find(m => m.months === +e.target.value))
          }
        >
          {monthsToPay.map(m => (
            <option
              key={m.months}
              value={m.months}
              selected={m.months === months.months}
            >
              {t('app.components.finance-selection.payment-term-options')}{' '}
              {getYearsToPay(m)}{' '}
              {t('app.components.finance-selection.payment-term-years')}
            </option>
          ))}
        </Select>
      </SelectRow>
      <Row>
        <P>{t('app.components.finance-selection.monthlyPayment')}</P>
        <span>
          <button onClick={() => setShowModal(true)}>
            <InfoIcon name="info" width="22" height="22" />
          </button>
          <Strong>
            {formatPrice({
              price: getMonthlyPayment(summary, months, financeId),
              country,
              digits: 2,
            })}
          </Strong>
        </span>
      </Row>
    </Wrapper>
  )
}

FinanceSelection.propTypes = {
  selection: PropTypes.shape({
    range: PropTypes.shape({ ifcAvailable: PropTypes.bool }),
  }).isRequired,
  summary: PropTypes.number.isRequired,
  months: PropTypes.number.isRequired,
  financeId: PropTypes.string.isRequired,
  financeOption: PropTypes.shape({}).isRequired,
  monthsToPayOptions: PropTypes.arrayOf(
    PropTypes.shape({ repaymentFactor: PropTypes.number.isRequired, months: PropTypes.number.isRequired })
  ).isRequired,
  setShowModal: PropTypes.func.isRequired,
  elementWidth: PropTypes.number.isRequired,
  handleFinanceOption: PropTypes.func.isRequired,
  handleMonths: PropTypes.func.isRequired,
}

export default FinanceSelection
